import { VidalyticsEmbed } from '@/components/VidalyticsEmbed';
import { useEffect, useRef, useState } from 'react';

const loomId = (src: string) => src.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/)?.[1] ?? src.split('/').pop()?.split('?')[0] ?? '';

/** Plain Loom embed URL, exactly as Loom's own embed code (no extra parameters). */
export const loomEmbedSrc = (src: string) => `https://www.loom.com/embed/${loomId(src)}`;

/** Loom's padding-bottom trick for the given aspect class. */
export const loomPadding = (aspect: string) =>
  aspect.includes('video') || aspect.includes('16/9') ? '56.25%' : aspect.includes('4/3') ? '75%' : '177.78%';

type Props = {
  src: string;
  /** aspect ratio class, defaults to vertical 9:16 */
  aspect?: string;
  className?: string;
  eager?: boolean;
  allowed?: boolean;
  order?: number;
  onLoaded?: () => void;
  title?: string;
};

/**
 * Mounts once the element is within ~1.5 screens of the viewport and then stays mounted.
 * iPhones choke (endless spinner, black picture with only sound) when a page starts
 * 15–25 Loom players at once, so each player only starts when the visitor gets near it.
 */
export const useNearViewport = <T extends Element>(skip = false) => {
  const ref = useRef<T>(null);
  const [near, setNear] = useState(skip);
  useEffect(() => {
    if (near) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setNear(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setNear(true);
          io.disconnect();
        }
      },
      { rootMargin: '150% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [near]);
  return [ref, near] as const;
};

/** Raw Loom embed, identical to the code Loom provides (started when the visitor gets near it). */
export const LoomVideo = ({ src, aspect = 'aspect-[9/16]', className = '', title = 'Client video', eager = false, allowed }: Props) => {
  const [ref, near] = useNearViewport<HTMLDivElement>(eager);
  const show = eager || (allowed ?? near);
  return (
    <div className={className}>
      <div ref={ref} style={{ position: 'relative', paddingBottom: loomPadding(aspect), height: 0 }}>
        {show && (
          <iframe
            src={loomEmbedSrc(src)}
            frameBorder="0"
            {...{ webkitallowfullscreen: 'true', mozallowfullscreen: 'true' }}
            allowFullScreen
            title={title}
            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
          />
        )}
      </div>
    </div>
  );
};

type CarouselItem = { src: string; label?: string; href?: string };

/** Manual (no autoplay) horizontal carousel of vertical videos, with wrap-around arrows + dots. */
export const LoomCarousel = ({ items }: { items: CarouselItem[] }) => {
  const seen = new Set<string>();
  const unique = items.filter((v) => {
    const id = loomId(v.src);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });

  const scroller = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const [limit, setLimit] = useState(3);
  const [rowRef, rowNear] = useNearViewport<HTMLDivElement>();
  const extend = (to: number) => setLimit((l) => Math.max(l, to));

  const goTo = (i: number) => {
    const el = scroller.current;
    if (!el) return;
    const total = unique.length;
    const next = ((i % total) + total) % total;
    const card = el.querySelectorAll('[data-card]')[next] as HTMLElement | undefined;
    if (!card) return;
    el.scrollTo({ left: card.offsetLeft - el.offsetLeft, behavior: 'smooth' });
    setActive(next);
    extend(next + 3);
  };

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
      const cards = Array.from(el.querySelectorAll('[data-card]')) as HTMLElement[];
      const center = el.scrollLeft + el.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      cards.forEach((c, i) => {
        const d = Math.abs(c.offsetLeft - el.offsetLeft + c.offsetWidth / 2 - center);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      });
        setActive((prev) => (prev === best ? prev : best));
        extend(best + 3);
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (raf) window.cancelAnimationFrame(raf);
      el.removeEventListener('scroll', onScroll);
    };
  }, [unique.length]);

  if (unique.length === 0) return null;

  return (
    <div ref={rowRef}>
      <div
        ref={scroller}
        className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-4 px-4 no-scrollbar"
      >
        {unique.map((v, i) => (
          <div key={v.src} data-card className="shrink-0 snap-center w-[260px] sm:w-[300px] md:w-[340px] text-center">
            {!v.src.includes('loom.com/') ? (
              <VidalyticsEmbed embedId={v.src} title={v.label ? `${v.label} — client interview` : `Client interview ${i + 1}`} />
            ) : <LoomVideo
              src={v.src}
              order={i}
              allowed={rowNear && i < limit}
              title={v.label ? `${v.label} — video` : `Client video ${i + 1}`}
            />}
            {v.label && (
              <div className="mt-4">
                {v.href ? (
                  <a
                    href={v.href}
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-4 py-2 text-sm font-bold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                  >
                    {v.label}
                    <span aria-hidden="true">↗</span>
                  </a>
                ) : (
                  <span className="inline-flex items-center rounded-full border border-border bg-secondary px-4 py-2 text-sm font-bold text-foreground/80">
                    {v.label}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {unique.length > 1 && (
        <>
          <div className="flex items-center justify-center gap-4 mt-6">
            <button
              type="button"
              onClick={() => goTo(active - 1)}
              aria-label="Previous video"
              className="w-11 h-11 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary/10 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M15.4 7.4 14 6l-6 6 6 6 1.4-1.4-4.6-4.6z" /></svg>
            </button>
            <button
              type="button"
              onClick={() => goTo(active + 1)}
              aria-label="Next video"
              className="w-11 h-11 rounded-full border-2 border-primary text-primary flex items-center justify-center hover:bg-primary/10 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M8.6 16.6 10 18l6-6-6-6-1.4 1.4 4.6 4.6z" /></svg>
            </button>
          </div>
          <div className="flex items-center justify-center gap-2 mt-4">
            {unique.map((v, i) => (
              <button
                key={v.src}
                type="button"
                onClick={() => goTo(i)}
                aria-label={`Go to video ${i + 1}`}
                className="min-h-6 min-w-6 flex items-center justify-center"
              >
                <span
                  className={`block h-2 rounded-full transition-all ${i === active ? 'w-6 bg-primary' : 'w-2 bg-primary/25 hover:bg-primary/50'}`}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
